import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { LanguageProvider } from "./context/LanguageContext";
import { TrackCreationProvider } from "./context/TrackCreationContext";
import myProjectsLogo from "./assets/images/creation-my-projects.png";
import { getProjects, loginAdmin } from "./api";
import { GlobalStateProvider } from "./context/GlobalStateContext";
import AppRoutes from "./routes/AppRoutes";
import "./styles/tokens.css";
import "./styles/ds-atoms.css";
import "./styles/app-shell.css";
import "./styles/responsive.css";
import "./styles/global.css";
import "./i18n"; // Import the i18n config
import { NotificationProvider } from "./context/NotificationContext";

// Define types for project data
interface Project {
    name: string;
    id: string;
}

interface ProjectLink {
    name: string;
    path: string;
    logo: string;
}

interface DropdownOption {
    label: string;
    value: string;
}

const App: React.FC = () => {
    const [isLoggedIn, setIsLoggedIn] = useState<boolean>(!!localStorage.getItem("token"));
    const [projectLinks, setProjectLinks] = useState<ProjectLink[]>([]);
    const [projects, setProjects] = useState<DropdownOption[]>([]);

    // Authentication management
    useEffect(() => {
        const handleAuthChange = () => {
            setIsLoggedIn(!!localStorage.getItem("token"));
        };

        window.addEventListener("storage", handleAuthChange);
        return () => window.removeEventListener("storage", handleAuthChange);
    }, []);

    // Fetch projects from API
    const fetchProjects = useCallback(async () => {
        const token = localStorage.getItem("token");
        if (!token) return;

        try {
            const response = await getProjects({ token });
            if (!response) return;

            const projectData: ProjectLink[] = response.map((project: Project) => ({
                name: project.name,
                path: `/projects/${project.id}`,
                logo: myProjectsLogo,
            }));

            const dropdownData: DropdownOption[] = response.map((project: Project) => ({
                label: project.name,
                value: project.id,
            }));

            setProjectLinks(projectData);
            setProjects(dropdownData);
        } catch (err) {
            console.error("Failed to fetch projects:", err);
        }
    }, []);

    // Login handler
    const handleLogin = async (email: string, password: string): Promise<boolean> => {
        try {
            const result = await loginAdmin({ email, passwordUncrypted: password });
            if (!result?.token) return false;

            localStorage.setItem("token", result.token);
            setIsLoggedIn(true);
            return true;
        } catch (error) {
            console.error("Login failed:", error);
            // Re-throw to allow Login component to handle specific errors (e.g., rate limiting)
            throw error;
        }
    };

    // Logout handler
    const handleLogout = () => {
        localStorage.removeItem("token");
        setIsLoggedIn(false);
    };

    return (
        <Router>
            <LanguageProvider>
                <GlobalStateProvider>
                    <TrackCreationProvider>
                        <NotificationProvider>
                            <AppRoutes
                                isLoggedIn={isLoggedIn}
                                projects={projects}
                                projectLinks={projectLinks}
                                handleLogin={handleLogin}
                                handleLogout={handleLogout}
                                fetchProjects={fetchProjects}
                            />
                        </NotificationProvider>
                    </TrackCreationProvider>
                </GlobalStateProvider>
            </LanguageProvider>
        </Router>
    );
};

export default App;
